import { PermissionGate } from "@components/PermissionGate";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import {
  ActionIcon,
  Badge,
  Button,
  Center,
  Checkbox,
  ComboboxItem,
  Group,
  Loader,
  Modal,
  ScrollAreaAutosize,
  Select,
  Stack,
  Tabs,
  TagsInput,
  Text,
  Textarea,
  TextInput,
} from "@mantine/core";
import { useForm, zodResolver } from "@mantine/form";
import { useDebouncedValue, useLogger, usePrevious } from "@mantine/hooks";
import {
  IconArrowLeft,
  IconCalendar,
  IconChevronDown,
  IconFilter,
  IconPencil,
  IconPlus,
  IconSearch,
  IconTrash,
  IconUser,
  IconX,
} from "@tabler/icons-react";
import { AnimatePresence, motion } from "framer-motion";
import i18n from "src/i18n";
import { z } from "zod";

import { formatDateToString } from "@utils/dateValidation";
import { renderLoadingErrorData } from "@utils/renderLoadingErrorData";

import { getDateRanges, getPatientNoteCategories } from "@data/dropdownValues";

import { useCreatePatientNote } from "@hooks/mutations/patientNote/useCreatePatientNote";
import { useDeletePatientNote } from "@hooks/mutations/patientNote/useDeletePatientNote";
import { useUpdatePatientNote } from "@hooks/mutations/patientNote/useUpdatePatientNote";
import { useGetPatientNotes } from "@hooks/queries/patientNote/useGetPatientNotes";
import { useGetPatientNoteTags } from "@hooks/queries/patientNote/useGetPatientNoteTags";
import { useGetPatientNoteTemplates } from "@hooks/queries/patientNote/useGetPatientNoteTemplates";

import { FollowUp } from "@customTypes/followUp";
import { PatientNote, PatientNoteTemplate } from "@customTypes/patientNote";

import {
  StyledDescription,
  StyledNoteCard,
  StyledPatientNotesModalContent,
} from "./PatientNotesModal.styled";


interface PatientNotesModalProps {
  opened: boolean;
  close: () => void;
  followUp?: Partial<FollowUp>;
  patientId?: string;
  appointmentId?: string;
}
export const PatientNotesModal = ({
  opened,
  close,
  followUp: initialFollowUp,
  patientId: initialPatientId,
  appointmentId: initialAppointmentId,
}: PatientNotesModalProps) => {
  const { t } = useTranslation();
  const { patientId: paramsPatientId } = useParams();

  const [patientId, setPatientId] = useState(
    paramsPatientId ?? initialPatientId
  );
  const [currentTab, setCurrentTab] = useState(1);
  const prevTab = usePrevious(currentTab);
  const [isInitialModalRender, setIsInitialModalRender] = useState(true);
  const [followUp, setFollowUp] = useState<Partial<FollowUp>>(
    initialFollowUp ?? {}
  );
  const [currentPatientNote, setCurrentPatientNote] = useState<
    Partial<PatientNote>
  >({});

  // Reset isInitialModalRender when modal is closed
  useEffect(() => {
    if (!opened) {
      setIsInitialModalRender(true);
    } else if (isInitialModalRender) {
      // Set to false after the modal has been opened
      setIsInitialModalRender(false);
    }
  }, [opened]);

  // Reset to first tab when modal is closed
  useEffect(() => {
    if (!opened) {
      setCurrentTab(1);
    }
  }, [opened]);

  useEffect(() => {
    if (initialFollowUp) {
      setFollowUp(initialFollowUp);
    }
  }, [initialFollowUp]);

  useEffect(() => {
    if (initialPatientId) setPatientId(initialPatientId);
  }, [initialPatientId]);

  return (
    <Modal
      centered
      title={<Text fw={500}>{t("patient-notes")}</Text>}
      opened={opened}
      onClose={close}
      size={"lg"}
      scrollAreaComponent={ScrollAreaAutosize}
      styles={{ body: { height: 600 - 60 } }} // This is the max-height of the modal set in App.tsx
    >
      <StyledPatientNotesModalContent>
        <AnimatePresence mode="wait">
          {currentTab === 1 && (
            <Tab1
              key={"tab1"}
              setCurrentTab={setCurrentTab}
              isInitialModalRender={isInitialModalRender}
              patientId={patientId ?? followUp?.patientId ?? ""}
              appointmentId={initialAppointmentId}
              currentPatientNote={currentPatientNote}
              setCurrentPatientNote={setCurrentPatientNote}
              cId={followUp?.cId ?? ""}
            />
          )}
          {currentTab === 2 && (
            <Tab2
              key={"tab2"}
              setCurrentTab={setCurrentTab}
              prevTab={prevTab}
              patientId={patientId ?? followUp?.patientId ?? ""}
              appointmentId={initialAppointmentId}
              currentPatientNote={currentPatientNote}
              setCurrentPatientNote={setCurrentPatientNote}
              cId={followUp?.cId ?? ""}
            />
          )}
          {currentTab === 3 && (
            <Tab3
              key={"tab3"}
              setCurrentTab={setCurrentTab}
              prevTab={prevTab}
              currentPatientNote={currentPatientNote}
              setCurrentPatientNote={setCurrentPatientNote}
              cId={followUp?.cId ?? ""}
              patientId={patientId ?? followUp?.patientId ?? ""}
            />
          )}
        </AnimatePresence>
      </StyledPatientNotesModalContent>
    </Modal>
  );
};

interface Props {
  setCurrentTab: (arg: number) => void;
  isInitialModalRender?: boolean;
  prevTab?: number;
  patientId?: string | undefined;
  appointmentId?: string | undefined;
  currentPatientNote?: Partial<PatientNote>;
  setCurrentPatientNote?: (arg: Partial<PatientNote>) => void;
  cId?: string | undefined;
}

// List tab
const Tab1 = ({
  setCurrentTab,
  isInitialModalRender,
  patientId,
  appointmentId,
  setCurrentPatientNote,
}: Props) => {
  const { t } = useTranslation();
  
  // Track if user has manually dismissed the filter
  const [isFilterDismissed, setIsFilterDismissed] = useState(false);

  // Reset dismissal when the appointmentId prop changes (e.g. opening a different appointment)
  useEffect(() => {
    setIsFilterDismissed(false);
  }, [appointmentId]);

  // Calculate the effective ID to use for filtering
  const activeAppointmentId = isFilterDismissed ? undefined : appointmentId;

  const [displayFilters, setDisplayFilters] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);
  const [dateRange, setDateRange] = useState<ComboboxItem | null>({
    label: "",
    value: "all-time",
  });
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 400);
  const [category, setCategory] = useState<ComboboxItem>();
  const [tag, setTag] = useState<ComboboxItem | null>(null);

  const { data: patientNoteTagsData } = useGetPatientNoteTags();
  const {
    data: patientNotes,
    isLoading: isGetPatientNotesLoading,
    isError: isGetPatientNotesError,
    isSuccess: isGetPatientNotesSuccess,
    // refetch: refetchPatientNotes,
    // isRefetching: isRefetchingPatientNotes,
    // isRefetchError: isRefetchPatientNotesError,
  } = useGetPatientNotes({
    patientId: patientId ?? "",
    appointmentId: activeAppointmentId,
    dateRange: dateRange?.value ?? "",
    search: debouncedSearch,
    category: category?.value === "all" ? "" : category?.value ?? "",
    tags: tag?.value,
    pagination: {
      page: 1,
      pageSize: 1000,
    },
  });

  // Set hasAnimated to true after component mounts
  useEffect(() => {
    setHasAnimated(true);
  }, []);

  // Skip animation if it's the initial render of the modal
  const shouldAnimate = !isInitialModalRender || hasAnimated;

  return (
    <motion.div
      initial={shouldAnimate ? { opacity: 0, x: -10 } : { opacity: 0, x: 0 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -10 }}
      transition={{
        duration: 0.15,
        exit: { duration: 0.1 },
        ease: "easeInOut",
      }}>
      <Stack pb="3rem" gap={0}>
        <Group justify="space-between">
          <Group gap={5}>
            <Button
              variant={"outline"}
              leftSection={<IconFilter size={16} stroke={2} />}
              rightSection={
                <IconChevronDown
                  size={16}
                  stroke={2}
                  style={{
                    rotate: displayFilters ? "180deg" : "0deg",
                    transition: "rotate 0.15s ease",
                  }}
                />
              }
              color="gray"
              onClick={() => setDisplayFilters(a => !a)}>
              {t("filters")}
            </Button>
            <TextInput
              leftSection={<IconSearch size={16} stroke={1.5} />}
              placeholder={t("search")}
              miw={250}
              flex={"1 0"}
              value={search}
              onChange={e => setSearch(e.target.value)}
              rightSection={
                search ? (
                  <ActionIcon
                    variant="subtle"
                    color="gray"
                    onClick={() => setSearch("")}>
                    <IconX size={18} stroke={1.5} />
                  </ActionIcon>
                ) : null
              }
            />
          </Group>
          <PermissionGate permission="notes.create"><Button
            leftSection={<IconPlus size={16} stroke={2} />}
            onClick={() => setCurrentTab(2)}>
            {t("add-note")}
          </Button></PermissionGate>
        </Group>

        <AnimatePresence>
          {displayFilters && (
            <motion.div
              initial={{ opacity: 0, y: -10, height: 0 }}
              animate={{ opacity: 1, y: 0, height: "auto" }}
              exit={{ opacity: 0, y: -10, height: 0 }}
              transition={{ duration: 0.2, ease: "easeInOut" }}
              style={{ overflow: "hidden" }} // Ensures smooth collapsing
            >
              <Group gap="xs" mt="1.5rem">
                <Select
                  w={189}
                  styles={{ label: { fontSize: 12 } }}
                  label={t("category")}
                  data={getPatientNoteCategories()}
                  value={category?.value ?? null}
                  onChange={(_, option) => setCategory(option)}
                />
                <Select
                  styles={{ label: { fontSize: 12 } }}
                  searchable
                  rightSectionPointerEvents="all"
                  rightSection={
                    tag ? (
                      <ActionIcon
                        color="gray"
                        variant="subtle"
                        onClick={() => setTag(null)}>
                        <IconX size={16} stroke={1.5} />
                      </ActionIcon>
                    ) : null
                  }
                  w={189}
                  label={t("tag")}
                  data={patientNoteTagsData ?? []}
                  value={tag?.value ?? null}
                  onChange={(_, option) => setTag(option)}
                />
                <Select
                  allowDeselect={false}
                  styles={{ label: { fontSize: 12 } }}
                  w={189}
                  label={t("date")}
                  data={getDateRanges().filter(
                    range => range.value !== "custom"
                  )}
                  rightSectionPointerEvents="all"
                  rightSection={
                    dateRange && dateRange?.value !== "all-time" ? (
                      <ActionIcon
                        color="gray"
                        variant="subtle"
                        onClick={() =>
                          setDateRange({ label: "", value: "all-time" })
                        }>
                        <IconX size={16} stroke={1.5} />
                      </ActionIcon>
                    ) : null
                  }
                  value={dateRange?.value ?? null}
                  onChange={(_, option) => setDateRange(option)}
                />
              </Group>
            </motion.div>
          )}
        </AnimatePresence>

        {activeAppointmentId && (
          <Group mt="md">
            <Badge
              size="lg"
              variant="light"
              color="accent"
              pr={3}
              rightSection={
                <ActionIcon
                  size="xs"
                  color="accent"
                  radius="xl"
                  variant="transparent"
                  onClick={() => setIsFilterDismissed(true)}>
                  <IconX size={14} />
                </ActionIcon>
              }>
              {t("current-appointment")}
            </Badge>
          </Group>
        )}

        <Stack mt={activeAppointmentId ? "1rem" : "1.5rem"}>
          {renderLoadingErrorData({
            isLoading: isGetPatientNotesLoading,
            isError: isGetPatientNotesError,
            isSuccess: isGetPatientNotesSuccess,
            dataLength: patientNotes?.newData?.length ?? 0,
            errorComponent: (
              <Text fz={14} c="dimmed">
                {t("patient-notes-error")}
              </Text>
            ),
            emptyDataComponent: (
              <Text fz={14} c="dimmed">
                {t("empty-patient-notes")}
              </Text>
            ),
            loadingComponent: (
              <Center>
                <Loader size={18} />
              </Center>
            ),
            dataComponent: patientNotes?.newData?.map(
              (note: Partial<PatientNote>, index: number) => (
                <StyledNoteCard
                  className="note-card"
                  key={index}
                  onClick={() => {
                    setCurrentTab(3);
                    setCurrentPatientNote && setCurrentPatientNote(note);
                  }}>
                  <Badge color="teal">{note.category}</Badge>
                  <Text mt="1.5rem" fz={14} fw={500} c="accent">
                    {note.cId}
                  </Text>
                  <Text fz={16} fw={500} mt="1rem">
                    {note.title}
                  </Text>
                  <StyledDescription>{note.description}</StyledDescription>
                  <Group gap={5} mt="1rem">
                    {note?.tags?.map(item => (
                      <Badge bg="gray.1" c="gray.7" key={item}>
                        {item}
                      </Badge>
                    ))}
                  </Group>

                  <Group mt="1.5rem">
                    <Group gap={5} align="center">
                      <IconUser size={15} stroke={2} color="gray" />
                      <Text fz={13} c="dimmed">
                        {note?.createdBy ?? "N/A"}
                      </Text>
                    </Group>
                    <Group gap={5} align="center">
                      <IconCalendar size={15} stroke={2} color="gray" />
                      <Text fz={13} c="dimmed">
                        {note.createdAt
                          ? formatDateToString(
                              note.createdAt,
                              true,
                              i18n.language
                            )
                          : "N/A"}
                      </Text>
                    </Group>
                  </Group>
                </StyledNoteCard>
              )
            ),
          })}
        </Stack>
      </Stack>
    </motion.div>
  );
};

const schema = () =>
  z.object({
    category: z.string().min(1, i18n.t("category-required")),
    title: z.string().min(1, "Title is required"),
    description: z.string().min(1, i18n.t("description-required")),
    tags: z.array(z.string()).optional(),
    isTemplate: z.boolean().optional(),
  });

type Inputs = z.infer<ReturnType<typeof schema>>;

// Add/Edit tab
const Tab2 = ({
  setCurrentTab,
  prevTab,
  patientId,
  currentPatientNote,
  setCurrentPatientNote,
  cId,
  appointmentId,
}: Props) => {
  const { t } = useTranslation();

  const form = useForm({
    mode: "controlled",
    validate: zodResolver(schema()),
    initialValues: {
      category: "",
      title: "",
      tags: [] as string[],
      description: "",
      isTemplate: false,
    },
  });
  const [currentTemplate, setCurrentTemplate] =
    useState<PatientNoteTemplate | null>(null);

  const {
    data: patientNoteTemplates,
    isLoading: isPatientNoteTemplatesLoading,
    fetchStatus: patientNoteTemplatesFetchStatus,
  } = useGetPatientNoteTemplates({
    category: form.getValues().category,
    isEnabled: !!form.getValues().category,
  });
  const { mutate: createPatientNote, isLoading: isCreatePatientNoteLoading } =
    useCreatePatientNote();
  const { mutate: updatePatientNote, isLoading: isUpdatePatientNoteLoading } =
    useUpdatePatientNote();

  const handleFormSubmit = (values: Inputs) => {
    // console.log(values);

    if (prevTab === 3) {
      // Update
      updatePatientNote(
        {
          patientId: patientId ?? "",
          patientNote: {
            id: currentPatientNote?.id,
            title: values.title,
            description: values.description,
            category: values.category,
            tags: values.tags,
          },
        },
        {
          onSuccess: (res: any) => {
            setCurrentPatientNote &&
              setCurrentPatientNote({
                category: res.data.type,
                cId: res.data.c_id,
                createdAt: res.data.created_at,
                id: res.data._id,
                description: res.data.description,
                patientId: res.data.patient_id,
                createdBy: res.data.created_by,
                tags: res.data.tags,
                title: res.data.title,
              });
            setCurrentTab(3);
          },
        }
      );
    } else {
      // Create
      createPatientNote(
        {
          cId: cId ?? "",
          patientId: patientId ?? "",
          appointmentId: appointmentId ?? "",
          patientNote: {
            title: values.title,
            description: values.description,
            category: values.category,
            tags: values.tags,
            isTemplate: values.isTemplate,
          },
        },
        {
          onSuccess: (res: any) => {
            setCurrentPatientNote &&
              setCurrentPatientNote({
                category: res.data.type,
                cId: res.data.c_id,
                createdAt: res.data.created_at,
                id: res.data._id,
                description: res.data.description,
                patientId: res.data.patient_id,
                createdBy: res.data.created_by,
                tags: res.data.tags,
                title: res.data.title,
              });
            setCurrentTab(3);
          },
        }
      );
    }
  };

  useEffect(() => {
    import.meta.env.DEV && console.log("[DEV] Form:", form.getValues());
  }, [form.getValues()]);

  useEffect(() => {
    if (prevTab === 3 && currentPatientNote && currentPatientNote?.id) {
      // console.log(currentPatientNote);
      form.setValues({
        title: currentPatientNote.title ?? "",
        category: currentPatientNote.category ?? "",
        description: currentPatientNote.description ?? "",
        tags: currentPatientNote.tags ?? [],
      });
    }
  }, [prevTab, currentPatientNote]);

  useEffect(() => {
    if (currentTemplate) {
      // console.log(currentTemplate);
      form.setValues({
        title: currentTemplate.title,
        description: currentTemplate.description,
        tags: currentTemplate.tags,
      });
    }
  }, [currentTemplate]);

  return (
    <motion.div
      initial={prevTab === 3 ? { opacity: 0, x: 10 } : { opacity: 0, x: 10 }}
      animate={{ opacity: 1, x: 0 }}
      exit={prevTab === 3 ? { opacity: 0, x: 10 } : { opacity: 0, x: 10 }}
      transition={{
        duration: 0.15,
        exit: { duration: 0.1 },
        ease: "easeInOut",
      }}>
      <Stack pb="1.5rem">
        <Stack>
          <Button
            w="fit-content"
            size="compact-sm"
            variant="subtle"
            leftSection={<IconArrowLeft size={16} stroke={1.5} />}
            onClick={() => {
              if (prevTab === 3) {
                setCurrentTab(3);
              } else {
                setCurrentTab(1);
              }
            }}>
            {t("back")}
          </Button>
          <Text fz={17} fw={500}>
            {prevTab === 1 ? t("add-note") : t("edit-note")}
          </Text>
        </Stack>

        <form
          onSubmit={form.onSubmit(
            values => handleFormSubmit(values),
            errors => {
              const firstErrorPath = Object.keys(errors)[0];
              form.getInputNode(firstErrorPath)?.focus();
            }
          )}>
          <Stack>
            <Select
              label={t("category")}
              placeholder={t("select-category")}
              data={getPatientNoteCategories().filter(
                item => item.value !== "all"
              )}
              withAsterisk
              {...form.getInputProps("category")}
              key={form.key("category")}
              allowDeselect={false}
            />
            <Select
              label={t("template")}
              placeholder={t("select-template")}
              disabled={!form.getValues().category}
              rightSection={
                isPatientNoteTemplatesLoading &&
                patientNoteTemplatesFetchStatus === "fetching" ? (
                  <Loader size={16} color="accent" />
                ) : null
              }
              data={patientNoteTemplates ?? []}
              value={currentTemplate?.value ?? null}
              onChange={(_val, option) =>
                setCurrentTemplate(
                  patientNoteTemplates?.find(
                    note => note.value === option.value
                  ) ?? null
                )
              }
              allowDeselect={false}
            />
            <TextInput
              label={t("title")}
              placeholder={t("type-title")}
              withAsterisk
              {...form.getInputProps("title")}
              key={form.key("title")}
            />
            <TagsInput
              label={t("tags")}
              placeholder={t("type-tags")}
              styles={{
                inputField: {
                  boxShadow: "none !important",
                },
              }}
              {...form.getInputProps("tags")}
              key={form.key("tags")}
            />
            <Textarea
              label={t("notes")}
              placeholder={t("type-notes")}
              rows={5}
              withAsterisk
              {...form.getInputProps("description")}
              key={form.key("description")}
            />
          </Stack>
          <Checkbox
            label={t("save-template")}
            mt="1rem"
            {...form.getInputProps("isTemplate")}
            key={form.key("isTemplate")}
          />

          <PermissionGate permission={prevTab === 3 ? "notes.edit" : "notes.create"}><Button
            mt="xl"
            w="100%"
            type="submit"
            loading={isCreatePatientNoteLoading || isUpdatePatientNoteLoading}>
            {t("save")}
          </Button></PermissionGate>
        </form>
      </Stack>
    </motion.div>
  );
};

// Detail tab
const Tab3 = ({
  setCurrentTab,
  prevTab,
  currentPatientNote,
  setCurrentPatientNote,
  patientId,
}: Props) => {
  const { t } = useTranslation();
  const [destinationTab, setDestinationTab] = useState(1);

  const {
    mutate: deletePatientNote,
    isLoading: isDeletingPatientNote,
    isSuccess: isDeletePatientNoteSuccess,
  } = useDeletePatientNote();

  useEffect(() => {
    if (isDeletePatientNoteSuccess) {
      setCurrentTab(1);
    }
  }, [isDeletePatientNoteSuccess]);

  return (
    <motion.div
      style={{ height: "100%" }}
      initial={prevTab === 2 ? { opacity: 0, x: -10 } : { opacity: 0, x: 10 }}
      animate={{ opacity: 1, x: 0 }}
      exit={
        destinationTab === 1 ? { opacity: 0, x: 10 } : { opacity: 0, x: -10 }
      }
      transition={{
        duration: 0.15,
        exit: { duration: 0.1 },
        ease: "easeInOut",
      }}>
      <Stack h="100%">
        <Button
          disabled={isDeletingPatientNote}
          w="fit-content"
          size="compact-sm"
          variant="subtle"
          leftSection={<IconArrowLeft size={16} stroke={1.5} />}
          onClick={() => {
            setCurrentTab(1);
            setDestinationTab(1);
          }}>
          {t("back")}
        </Button>

        <Group justify="space-between">
          <Badge color="teal">{currentPatientNote?.category}</Badge>
          <Group gap={5}>
            <PermissionGate permission="notes.edit"><ActionIcon
              variant="outline"
              disabled={isDeletingPatientNote}
              onClick={() => {
                if (currentPatientNote) {
                  setCurrentPatientNote?.(currentPatientNote);
                }
                setCurrentTab(2);
                setDestinationTab(2);
              }}>
              <IconPencil size={16} stroke={1.5} />
            </ActionIcon></PermissionGate>
            <PermissionGate permission="notes.delete"><ActionIcon
              color="red"
              variant="outline"
              loading={isDeletingPatientNote}
              onClick={() => {
                deletePatientNote({
                  patientNoteId: currentPatientNote?.id ?? "",
                  patientId: patientId ?? "",
                });
              }}>
              <IconTrash size={16} stroke={1.5} />
            </ActionIcon></PermissionGate>
          </Group>
        </Group>

        <Text fz={14} c="accent" fw={500}>
          {currentPatientNote?.cId ?? "N/A"}
        </Text>
        <Text fz={17} fw={500}>
          {currentPatientNote?.title ?? "N/A"}
        </Text>
        <Text fz={14} lh={1.4}>
          {currentPatientNote?.description ?? "N/A"}
        </Text>

        <Text fz={14} fw={500} mt={"1.5rem"}>
          {t("tags")}:
        </Text>
        <Group gap={5}>
          {currentPatientNote?.tags?.map(item => (
            <Badge bg="gray.1" c="gray.7" key={item}>
              {item}
            </Badge>
          ))}
        </Group>

        <Group mt="auto">
          <Group gap={5} align="center">
            <IconUser size={15} stroke={2} color="gray" />
            <Text fz={13} c="dimmed">
              {currentPatientNote?.createdBy ?? "N/A"}
            </Text>
          </Group>
          <Group gap={5} align="center">
            <IconCalendar size={15} stroke={2} color="gray" />
            <Text fz={13} c="dimmed">
              {currentPatientNote?.createdAt
                ? formatDateToString(
                    currentPatientNote.createdAt,
                    true,
                    i18n.language
                  )
                : "N/A"}
            </Text>
          </Group>
        </Group>
      </Stack>
    </motion.div>
  );
};
